import React, { useEffect, useState } from "react";
import { Route, Switch, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core";
import grey from "@material-ui/core/colors/grey.js";
import ReactCountryFlag from "react-country-flag";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ExpandMoreRoundedIcon from "@material-ui/icons/ExpandMoreRounded";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import clsx from "clsx";
import { useTranslate } from "react-polyglot";
import logo from "../../assets/logo.svg";
import studyLessLogo from "../../assets/study-less-512x512.png";
import rusLangIcon from "../../assets/ic_rus_lang.svg";
import uzLangIcon from "../../assets/UZ_flag.png";
import FlexboxVertical from "../../SharedComponents/FlexboxVertical.jsx";
import FlexboxHorizontal from "../../SharedComponents/FlexboxHorizontal.jsx";
import { useLocale } from "../../Providers/i18n/I18nProvider.jsx";
import CheckEmail from "./Components/CheckEmail.jsx";
import { useAuth } from "../../Providers/Auth/AuthProvider.jsx";
import Password from "./Components/Password.jsx";
import Registration from "./Components/Registration.jsx";
import DemoRegistrationPopUp from "./Components/DemoRegistrationPopUp.jsx";
import useEffectWithPreviousValues from "../../hooks/useEffectWithPreviousValues.js";
import { useBaseData } from "../../Providers/Data/BaseDataProvider.jsx";
import useEffectAsync from "../../hooks/useEffectAsync.js";
import useCompanyPublicId from "../../hooks/useCompanyPublicId.js";
import { IS_APP_SKILLGEN, IS_APP_STUDYLESS } from "../../constants.js";
import useTitle from "../../hooks/useTitle.js";
import useFavicon from "../../hooks/useFavicon.js";
import ViewportMeta from "../../SharedComponents/ViewportMeta.jsx";
import { isCompanySkillzon } from "../../Utils/misc.js";
import { useDialogs } from "../../Providers/Common/DialogsProvider.jsx";

const useStyles = makeStyles((theme) => ({
  boxRoot: {
    backgroundColor: grey[50],
    minHeight: "100vh",
    "& .MuiTextField-root": {
      backgroundColor: grey[200],
      margin: "30px 20px 0",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 0,
    },
  },
  logo: {
    maxHeight: 130,
  },
  tabs: {
    marginTop: 31,
    backgroundColor: grey[200],
    "& .Mui-selected": {
      fontWeight: 800,
    },
    "& .MuiTab-root": {
      fontSize: 14,
    },
  },
  langs: {
    margin: "40px 0",
  },
  form: {
    backgroundColor: "white",
    "& button": {
      marginTop: 30,
    },
  },
  modernPage: {
    minHeight: "100vh",
    backgroundColor: "#F6F6F6",
  },
  modernHeader: {
    boxSizing: "border-box",
    minHeight: 128,
    padding: "40px 24px",
    gap: 40,
  },
  modernHeaderWithLogo: {
    minHeight: 388,
    paddingTop: 80,
  },
  modernLogo: {
    width: 180,
    height: 180,
    borderRadius: 30,
    objectFit: "contain",
  },
  passwordHeader: {
    position: "relative",
    boxSizing: "border-box",
    width: "100%",
    minHeight: 388,
    paddingTop: 80,
  },
  passwordBackButton: {
    position: "absolute",
    top: 5,
    left: 6,
    width: 35,
    height: 35,
    padding: 6,
    color: "#1B1C1C",
    "& .MuiSvgIcon-root": {
      width: 24,
      height: 24,
    },
  },
  registrationHeader: {
    position: "relative",
    boxSizing: "border-box",
    width: "100%",
    minHeight: 65,
  },
  modernPanel: {
    boxSizing: "border-box",
    flex: 1,
    width: "100%",
    maxWidth: 800,
    alignSelf: "center",
    padding: "30px 25px 50px",
    backgroundColor: "#FFFFFF",
    borderRadius: "40px 40px 0 0",
  },
  registrationPanel: {
    padding: "20px 25px 0",
  },
  languageButton: {
    width: 180,
    height: 48,
    padding: 10,
    color: "#000000",
    backgroundColor: "#FFFFFF",
    border: "1px solid #CCCCCC",
    borderRadius: 16,
    textTransform: "none",
    justifyContent: "flex-start",
    "&:hover": {
      backgroundColor: "#FFFFFF",
    },
    "& .MuiButton-label": {
      fontSize: 16,
      fontWeight: 400,
    },
  },
  languageFlag: {
    flex: "0 0 auto",
    width: 24,
    height: 24,
    marginRight: 10,
    borderRadius: 6,
    objectFit: "cover",
  },
  languageChevron: {
    marginLeft: "auto",
  },
  languageMenuPaper: {
    width: 180,
    marginTop: 5,
    overflow: "hidden",
    border: "1px solid #CCCCCC",
    borderRadius: 16,
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
  },
  languageMenuList: {
    padding: 0,
  },
  languageMenuItem: {
    boxSizing: "border-box",
    minHeight: 48,
    padding: 10,
    fontSize: 16,
    "&.Mui-selected": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  languageCheck: {
    marginLeft: "auto",
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
}));

const useFlagsStyles = makeStyles((theme) => ({
  flagRoot: {
    objectFit: "cover",
    borderRadius: "50%",
  },
  active: {
    boxShadow: `0px 0px 0px 4px ${grey[50]}, 0px 0px 0px 8px ${theme.palette.primary.dark}`,
  },
}));

//because ReactCountryFlag overrides styles
const flagInlineStyles = { width: 40, height: 40 };

const languageNames = {
  en: "English",
  fr: "Français",
  uk: "Українська",
  ru: "Русский",
  uz: "Oʻzbekcha",
  de: "Deutsch",
  tr: "Türkçe",
  pl: "Polski",
  it: "Italiano",
};

const authLanguageCountryMap = {
  en: "US",
  fr: "FR",
  uk: "UA",
  de: "DE",
  tr: "TR",
  pl: "PL",
  it: "IT",
};

/** Выбор языка в стиле Android-экрана входа. */
const AuthLanguageSwitcher = () => {
  const classes = useStyles();
  const { locale, setLocale, localeCodes } = useLocale();
  const [anchorEl, setAnchorEl] = useState(null);

  if (localeCodes.length < 2) return null;

  /** @param {String} lang */
  const renderFlag = (lang) => {
    const commonProps = { className: classes.languageFlag, style: { width: 24, height: 24 }, alt: "" };

    if (lang === "ru") return <img {...commonProps} src={rusLangIcon} />;
    if (lang === "uz") return <img {...commonProps} src={uzLangIcon} />;

    return <ReactCountryFlag {...commonProps} countryCode={authLanguageCountryMap[lang]} svg />;
  };

  /** @param {String} lang */
  const selectLanguage = (lang) => {
    setLocale(lang);
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        className={classes.languageButton}
        aria-controls="auth-language-menu"
        aria-haspopup="true"
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {renderFlag(locale)}
        {languageNames[locale] || locale}
        <ExpandMoreRoundedIcon className={classes.languageChevron} />
      </Button>
      <Menu
        id="auth-language-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: classes.languageMenuPaper }}
        MenuListProps={{ className: classes.languageMenuList }}
      >
        {localeCodes.map((lang) => (
          <MenuItem
            className={classes.languageMenuItem}
            key={lang}
            selected={lang === locale}
            onClick={() => selectLanguage(lang)}
          >
            {renderFlag(lang)}
            {languageNames[lang] || lang}
            {lang === locale && <span className={classes.languageCheck}>✓</span>}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/** */
const LangsSwitcher = (props) => {
  const classes = useFlagsStyles();
  const { locale, setLocale, localeCodes } = useLocale();

  useEffect(() => {
    document.body.style.overflow = "auto";

    return () => {
      document.body.style.overflow = null;
    };
  }, []);

  /** @param {String} lang */
  const changeLocale = (lang) => () => {
    setLocale(lang);
  };

  // Конфигурация языков с флагами
  const langConfigs = [
    { code: "en", countryCode: "GB" },
    { code: "fr", countryCode: "FR" },
    { code: "uk", countryCode: "UA" },
    { code: "ru", customIcon: rusLangIcon },
    { code: "uz", customIcon: uzLangIcon },
    { code: "de", countryCode: "DE" },
    { code: "tr", countryCode: "TR" },
    { code: "pl", countryCode: "PL" },
    { code: "it", countryCode: "IT" },
  ];

  return (
    <FlexboxHorizontal {...props}>
      {langConfigs.map(({ code, countryCode, customIcon }) =>
        localeCodes.includes(code) ? (
          <IconButton key={code} onClick={changeLocale(code)}>
            {customIcon ? (
              <img
                src={customIcon}
                className={clsx(classes.flagRoot, { [classes.active]: locale === code })}
                style={flagInlineStyles}
                alt=""
              />
            ) : (
              <ReactCountryFlag
                className={clsx(classes.flagRoot, { [classes.active]: locale === code })}
                countryCode={countryCode}
                svg
                style={flagInlineStyles}
              />
            )}
          </IconButton>
        ) : null,
      )}
    </FlexboxHorizontal>
  );
};

/** */
const AuthPage = () => {
  const { warn } = useDialogs();
  const t = useTranslate();
  const { companyName, domain } = useCompanyPublicId();
  const classes = useStyles();
  const history = useHistory();
  /** @type {String} */
  const location = history.location.pathname;
  const { checkEmailResponse, user, signOut, company2, company } = useAuth();
  const { preselectedSubject, chooseSubject, getAvailableSubjects } = useBaseData();

  const [openDemoDialog, setOpenDemoDialog] = useState(false);

  const companyLogo = IS_APP_STUDYLESS ? studyLessLogo : logo;
  const modernCompanyLogo = (company && company.logo.url) || (company2 && company2.logo.url) || companyLogo;
  const isCheckEmailPage = /\/auth$/.test(location);
  const isPasswordPage = /\/password$/.test(location);
  const isRegistrationPage = /\/registration$/.test(location);

  useTitle("title.login", {
    school: company2?.name || company?.name || (IS_APP_STUDYLESS ? "Study Less" : "Skillzrun"),
  });

  useFavicon();

  // reset checking email data when user comes back from anywhere
  useEffectWithPreviousValues(
    ([prevLocation]) => {
      if (!companyName && !domain && !/\/auth$/.test(prevLocation) && /\/auth$/.test(location) && checkEmailResponse) {
        // Разлогин только для общего скилзрана (!companyName && !domain)
        signOut();
      }
    },
    [location],
  );

  // go to subject select if logged in or successfully registered
  useEffectAsync(async () => {
    // На Skillgen перенаправляем со страницы входа только пользователя с токеном.
    if (user && (!IS_APP_SKILLGEN || user.token)) {
      // if (preselectedOffer && schoolName) {
      //   history.replace(`/offer/${schoolName}/${preselectedOffer}`);
      // } else
      if (user.subjectId === -1) {
        if (preselectedSubject) {
          history.replace(`/subjects/${preselectedSubject}`);
        } else {
          history.replace("/subjects");
        }
      } else if ((domain || companyName) && preselectedSubject) {
        const availableSubjects = await getAvailableSubjects();

        const exist = availableSubjects && availableSubjects.find((available) => available.id === preselectedSubject);

        if (exist !== undefined && exist.added) {
          if (await chooseSubject(preselectedSubject)) {
            history.replace("/");
          }
        } else {
          history.replace("/");
        }
      } else if (!domain && !companyName && preselectedSubject && (await chooseSubject(preselectedSubject))) {
        history.replace("/");
      } else {
        history.replace("/subjects");
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // auth routes resolver
  useEffectWithPreviousValues(
    ([_, prevLocation]) => {
      if (
        /\/auth$/.test(prevLocation) &&
        /\/auth$/.test(location) &&
        checkEmailResponse &&
        (!checkEmailResponse.created || checkEmailResponse.invited)
      ) {
        // ==== Если школа Скиллзран, то через диалоговое окно можно зарегистрироваться и получить демо-доступ ====
        if (checkEmailResponse.company.id === 1) {
          setOpenDemoDialog(true);
          return;
        }
        // ===================================================================================

        // Регистрацию в скилзон запрещаем
        if (isCompanySkillzon(checkEmailResponse.company)) {
          warn(t("emailIsNotRegistered"));
          return;
        }

        history.push(`${companyName ? "/" + companyName : ""}/registration`);
        return;
      }
      if (
        /\/auth$/.test(prevLocation) &&
        /\/auth$/.test(location) &&
        checkEmailResponse &&
        checkEmailResponse.created &&
        !checkEmailResponse.invited
      ) {
        history.push(`${companyName ? "/" + companyName : ""}/password`);
      }
      if (
        /\/registration$/.test(location) &&
        (!checkEmailResponse || (checkEmailResponse.created && !checkEmailResponse.invited))
      ) {
        if (history.length > 2) {
          history.goBack();
        } else {
          history.replace(`${companyName ? "/" + companyName : ""}/auth`);
        }
      }
    },
    [checkEmailResponse, location],
  );

  if (isCheckEmailPage) {
    return (
      <>
        <ViewportMeta initialScale={1} />
        <FlexboxVertical className={classes.modernPage}>
          <FlexboxVertical
            className={clsx(classes.modernHeader, { [classes.modernHeaderWithLogo]: modernCompanyLogo })}
            alignItems="center"
            justifyContent="center"
          >
            {modernCompanyLogo && <img className={classes.modernLogo} src={modernCompanyLogo} alt="logo" />}
            <AuthLanguageSwitcher />
          </FlexboxVertical>
          <FlexboxVertical className={classes.modernPanel} alignItems="center">
            <CheckEmail />
          </FlexboxVertical>
        </FlexboxVertical>
        <DemoRegistrationPopUp open={openDemoDialog} onClose={() => setOpenDemoDialog(false)} />
      </>
    );
  }

  if (isPasswordPage) {
    return (
      <>
        <ViewportMeta initialScale={1} />
        <FlexboxVertical className={classes.modernPage}>
          <FlexboxVertical className={classes.passwordHeader} alignItems="center">
            <IconButton
              className={classes.passwordBackButton}
              aria-label={t("back")}
              onClick={() => history.push(`${companyName ? "/" + companyName : ""}/auth`)}
            >
              <ArrowBackIcon />
            </IconButton>
            {modernCompanyLogo && <img className={classes.modernLogo} src={modernCompanyLogo} alt="logo" />}
          </FlexboxVertical>
          <FlexboxVertical className={classes.modernPanel} alignItems="center">
            <Password />
          </FlexboxVertical>
        </FlexboxVertical>
        <DemoRegistrationPopUp open={openDemoDialog} onClose={() => setOpenDemoDialog(false)} />
      </>
    );
  }

  if (isRegistrationPage) {
    return (
      <>
        <ViewportMeta initialScale={1} />
        <FlexboxVertical className={classes.modernPage}>
          <FlexboxVertical className={classes.registrationHeader}>
            <IconButton
              className={classes.passwordBackButton}
              aria-label={t("back")}
              onClick={() => history.push(`${companyName ? "/" + companyName : ""}/auth`)}
            >
              <ArrowBackIcon />
            </IconButton>
          </FlexboxVertical>
          <FlexboxVertical className={clsx(classes.modernPanel, classes.registrationPanel)} alignItems="center">
            {modernCompanyLogo && <img className={classes.modernLogo} src={modernCompanyLogo} alt="logo" />}
            <Registration />
          </FlexboxVertical>
        </FlexboxVertical>
        <DemoRegistrationPopUp open={openDemoDialog} onClose={() => setOpenDemoDialog(false)} />
      </>
    );
  }

  return (
    <>
      <ViewportMeta
        // Жёсткая фиксация масштаба страницы для мобильных браузеров
        initialScale={0.7}
      />
      <FlexboxVertical className={classes.boxRoot} alignItems="center" justifyContent="center">
        <img
          className={classes.logo}
          src={(company && company.logo.url) || (company2 && company2.logo.url) || companyLogo}
          alt="logo"
        />
        <LangsSwitcher className={classes.langs} />
        <FlexboxVertical width={413} marginTop="20px">
          <Switch>
            <Route exact path={["/auth", "/:companyName/auth"]}>
              <CheckEmail className={classes.form} />
            </Route>
            <Route exact path={["/password", "/:companyName/password"]}>
              <Password className={classes.form} />
            </Route>
            <Route exact path={["/registration", "/:companyName/registration"]}>
              <Registration className={classes.form} />
            </Route>
          </Switch>
        </FlexboxVertical>
      </FlexboxVertical>
      <DemoRegistrationPopUp open={openDemoDialog} onClose={() => setOpenDemoDialog(false)} />
    </>
  );
};

export default AuthPage;
